import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, KeyRound, LogOut, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Logo } from "@/components/site/Logo";
import { adminApi, errorText } from "./adminApi";
import { RequestsTable, WaitlistTable, toCsv } from "./Tables";
import { PasswordDialog } from "./PasswordDialog";

const download = (name, text) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([text], { type: "text/csv" }));
  a.download = name;
  a.click();
};

const Stat = ({ label, value, testid, accent, sub }) => (
  <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-3 sm:p-5">
    <p className="text-[11px] sm:text-xs uppercase tracking-[0.12em] sm:tracking-[0.2em] font-mono text-slate-400">{label}</p>
    <p data-testid={testid} className={`font-display text-3xl sm:text-4xl font-extrabold mt-2 ${accent || ""}`}>{value}</p>
    {sub && <p data-testid={`${testid}-sub`} className="text-xs text-slate-400 mt-1">{sub}</p>}
  </div>
);

export const AdminDashboard = ({ user, onLogout }) => {
  const [reqs, setReqs] = useState([]);
  const [wait, setWait] = useState([]);
  const [q, setQ] = useState("");
  const [pw, setPw] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([adminApi.requests(), adminApi.waitlist()]);
      setReqs(Array.isArray(a.data) ? a.data : []);
      setWait(Array.isArray(b.data) ? b.data : []);
    } catch (ex) {
      if (ex?.response?.status === 401) onLogout();
      else toast.error(errorText(ex));
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const match = (o) => !q || JSON.stringify(o).toLowerCase().includes(q.toLowerCase());
  const fr = useMemo(() => reqs.filter(match), [reqs, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const fw = useMemo(() => wait.filter(match), [wait, q]); // eslint-disable-line react-hooks/exhaustive-deps

  const onStatus = async (id, status) => {
    setReqs((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
    try { await adminApi.setStatus(id, status); toast.success(`Marked as ${status}`); } catch (ex) { toast.error(errorText(ex)); load(); }
  };
  const onRebate = async (id, paid) => {
    if (paid && !window.confirm("Mark the $10 rebate as paid for this tester?")) return;
    try {
      const { data } = await adminApi.setRebate(id, paid);
      setReqs((rs) => rs.map((r) => (r.id === id ? { ...r, rebate_paid: data.rebate_paid, rebate_paid_at: data.rebate_paid_at } : r)));
      toast.success(paid ? "Rebate marked as paid" : "Rebate marked as not paid");
    } catch (ex) { toast.error(errorText(ex)); }
  };
  const owed = reqs.filter((r) => r.status === "tested" && !r.rebate_paid).length;
  const paidCount = reqs.filter((r) => r.rebate_paid).length;
  const shown = filter === "owed" ? fr.filter((r) => r.status === "tested" && !r.rebate_paid) : fr;

  const delReq = async (id) => {
    if (!window.confirm("Delete this test request?")) return;
    await adminApi.delRequest(id); setReqs((rs) => rs.filter((r) => r.id !== id)); toast.success("Request deleted");
  };
  const delWait = async (id) => {
    if (!window.confirm("Remove this sign-up from the waitlist?")) return;
    await adminApi.delWaitlist(id); setWait((ws) => ws.filter((w) => w.id !== id)); toast.success("Sign-up removed");
  };

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-white/10 sticky top-0 z-20 bg-[#070A14]/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-5 py-4 flex items-center justify-between gap-2">
          <Logo />
          <div className="flex items-center gap-1">
            <span className="hidden md:inline text-sm text-slate-400 mr-3">{user?.email}</span>
            <button data-testid="admin-refresh" onClick={load} aria-label="Refresh" className="p-2.5 rounded-full hover:bg-white/10"><RefreshCw size={18} /></button>
            <button data-testid="admin-change-password" onClick={() => setPw(true)} aria-label="Change password" className="p-2.5 rounded-full hover:bg-white/10"><KeyRound size={18} /></button>
            <button data-testid="admin-logout" onClick={onLogout} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 p-2.5 sm:px-4 sm:py-2 text-sm hover:bg-white/10 whitespace-nowrap"><LogOut size={16} /> <span className="hidden sm:inline">Log out</span></button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-10">
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Dashboard</h1>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mt-8">
          <Stat label="Requests" value={reqs.length} testid="stat-requests" />
          <Stat label="New" value={reqs.filter((r) => (r.status || "new") === "new").length} testid="stat-new" accent="text-[#FF2A8D]" />
          <Stat label="Waitlist" value={wait.length} testid="stat-waitlist" accent="text-[#5B95D6]" />
          <Stat label="Rebates owed" value={`$${owed * 10}`} testid="stat-rebates-owed" accent="text-[#FFD466]" sub={`${owed} owed · $${paidCount * 10} paid so far`} />
        </div>

        <Tabs defaultValue="requests" className="mt-10">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
            <TabsList className="bg-white/5 rounded-full p-1 h-auto w-fit">
              <TabsTrigger data-testid="tab-requests" value="requests" className="rounded-full px-4 py-2 data-[state=active]:bg-[#FF2A8D] data-[state=active]:text-white">Test requests</TabsTrigger>
              <TabsTrigger data-testid="tab-waitlist" value="waitlist" className="rounded-full px-4 py-2 data-[state=active]:bg-[#FF2A8D] data-[state=active]:text-white">Waitlist</TabsTrigger>
            </TabsList>
            <div className="relative sm:w-72">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, city…"
                className="w-full rounded-full bg-white/5 border border-white/10 pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#FF2A8D]" />
            </div>
          </div>
          <TabsContent value="requests">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              {[["all", "All requests"], ["owed", `Rebates owed (${owed})`]].map(([v, l]) => (
                <button key={v} data-testid={`filter-${v}`} onClick={() => setFilter(v)}
                  className={`rounded-full px-3.5 py-1.5 text-sm transition-colors ${filter === v ? "bg-white text-[#070A14] font-semibold" : "border border-white/15 text-slate-300 hover:border-white/40"}`}>{l}</button>
              ))}
              <button data-testid="export-requests" onClick={() => download("test-requests.csv", toCsv(shown, ["name", "email", "phone", "city", "preferred_date", "package", "status", "coupon", "rebate_paid", "rebate_paid_at", "created_at"]))}
                className="ml-auto inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white"><Download size={16} /> Export CSV</button>
            </div>
            <RequestsTable rows={shown} onStatus={onStatus} onDelete={delReq} onRebate={onRebate} empty={filter === "owed" ? "No rebates owed — you're all caught up." : "No test requests yet."} />
          </TabsContent>
          <TabsContent value="waitlist">
            <button data-testid="export-waitlist" onClick={() => download("waitlist.csv", toCsv(fw, ["email", "role", "created_at"]))}
              className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white"><Download size={16} /> Export CSV</button>
            <WaitlistTable rows={fw} onDelete={delWait} />
          </TabsContent>
        </Tabs>
      </main>
      <PasswordDialog open={pw} onOpenChange={setPw} />
    </div>
  );
};
